import { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel, StatusPill, EmptyState } from "../../components/Bits";
import { Modal } from "../admin/Students";
import { api, formatApiError } from "../../lib/api";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function StudentLeaves() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "leave", from_date: "", to_date: "", reason: "", destination: "", contact_during_leave: "" });
  const [saving, setSaving] = useState(false);

  async function load() { const { data } = await api.get("/leaves"); setRows(data); }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/leaves", form);
      toast.success("Application submitted. Awaiting warden's approval.");
      setOpen(false);
      setForm({ type: "leave", from_date: "", to_date: "", reason: "", destination: "", contact_during_leave: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <AppShell>
      <PageHeader
        overline="Time away"
        title="Leave & day-out"
        subtitle="Going home for a few days, or stepping out for the day?"
        actions={<button onClick={() => setOpen(true)} data-testid="apply-leave-btn" className="bg-terracotta text-white px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"><Plus className="h-4 w-4" /> New application</button>}
      />

      <Panel>
        {rows === null ? <div className="text-sm text-warm">Loading…</div>
          : rows.length === 0 ? <EmptyState title="No applications" hint="Submit a leave or day-out request whenever you need." />
            : (
              <ul className="divide-y divide-[#EAE5DE]">
                {rows.map((l) => (
                  <li key={l.id} className="py-4 flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2"><span className="text-xs capitalize text-deep bg-[#F4F1EA] px-2.5 py-0.5 rounded-full">{l.type.replace("_", " ")}</span><StatusPill status={l.status} /></div>
                      <div className="font-medium text-deep mt-2">{l.from_date} → {l.to_date}</div>
                      <div className="text-sm text-warm">{l.reason}</div>
                      {l.destination && <div className="text-xs text-warm mt-1">Destination: {l.destination}</div>}
                      {l.admin_note && <div className="mt-2 text-sm bg-[#F4F1EA] border border-warm rounded-md px-3 py-2"><span className="text-xs uppercase tracking-wider text-[#A67B5B]">Warden note </span>{l.admin_note}</div>}
                    </div>
                    <div className="text-xs text-warm whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</div>
                  </li>
                ))}
              </ul>
            )}
      </Panel>

      {open && (
        <Modal title="New leave / day-out application" onClose={() => setOpen(false)}>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
            <FieldL label="Type *">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={input} data-testid="leave-type">
                <option value="leave">Leave</option><option value="day_out">Day-out</option>
              </select>
            </FieldL>
            <FieldL label="Destination"><input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className={input} placeholder="Where are you going?" /></FieldL>
            <FieldL label="From *"><input required type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className={input} /></FieldL>
            <FieldL label="To *"><input required type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className={input} /></FieldL>
            <div className="sm:col-span-2"><FieldL label="Reason *"><textarea required rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={input} data-testid="leave-reason" /></FieldL></div>
            <div className="sm:col-span-2"><FieldL label="Contact during leave"><input value={form.contact_during_leave} onChange={(e) => setForm({ ...form, contact_during_leave: e.target.value })} className={input} placeholder="phone number where we can reach you" /></FieldL></div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-5 py-2 rounded-md text-sm border border-warm">Cancel</button>
              <button disabled={saving} type="submit" data-testid="submit-leave-btn" className="bg-terracotta text-white px-6 py-2 rounded-md text-sm disabled:opacity-60">{saving ? "Submitting…" : "Submit"}</button>
            </div>
          </form>
        </Modal>
      )}
    </AppShell>
  );
}

const input = "w-full px-3 py-2 rounded-md border border-warm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8C2B8] focus:border-[#C86F58] text-sm";
function FieldL({ label, children }) { return <label className="block"><span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">{label}</span>{children}</label>; }
